/**
 * Accordion Variant
 * This is an variant of the original accordion with different colors, icons.
 * Same behaviour as the other one
 * Accordion V2 is probably a bad name convention :D
 */

import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { Plus } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn("border-b border-transparent", className)}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    number?: string;
  }
>(({ className, children, number, ...props }, ref) => (
  <AccordionPrimitive.Header>
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "[&[data-state=open]]:bg-primary group flex w-full items-center justify-between rounded-lg p-4 text-left text-base font-medium transition-all hover:bg-slate-100 [&[data-state=open]]:rounded-b-none [&[data-state=open]]:text-white",
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-6 text-xl font-semibold">
        <span className="text-muted-foreground text-3xl font-semibold group-data-[state=open]:text-white/70">
          {number?.padStart(2, "0")}
        </span>
        {children}
      </div>
      <Plus className="bg-primary text-background group-data-[state=open]:text-foreground group-data-[state=open]:bg-background h-7 w-7 shrink-0 rounded-full p-2 transition-transform duration-200 group-data-[state=open]:rotate-45" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down bg-primary overflow-hidden rounded-b-lg pl-11 text-sm text-white transition-all",
      className,
    )}
    {...props}
  >
    <div className="p-4 pt-0">{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
